import { apiClient } from "@/http";
import type { JsonObject } from "./common";
import type { PageQuery, PageResp } from "./page";
import type { RegistryDatasetRef } from "./training";
import type { TrainingBudgetUnit } from "./trainingPolicies";
import { apiRoutes } from "./apiRoutes";
import type { TrainingEvalRunScope } from "@/modules/training/public";

// 평가(eval) API. eval-run 은 source_session 에 귀속된다. 스코프는 그 세션이 actor 가 접근할 수
// 있는 스코프인지로 정한다. 큰 byte(영상·아티팩트)는 signed URL 로 직접 받는다. 로그는 커서
// 폴링으로 받는다(held-open 스트림 아님). 시나리오 카탈로그 read는 전역 auth-only, create는
// member-write다.

export type EvalRunKind = "general" | "dataset_bound";

export type TrainingEvalRunView = {
  id: string;
  source_session_id: string | null;
  scenario: string | null;
  scenario_id: string | null;
  kind: EvalRunKind | null;
  dataset_id: string | null;
  source_eval_run_id: string | null;
  status: string;
  mlflow_run_id: string | null;
  // 평가 실행 입력(체크포인트·seed·num_episodes). registry 는 해석하지 않고 그대로 전달한다.
  // "무엇을 평가했나"와 run 간 파라미터 차이를 이 값에서 읽는다.
  params: JsonObject;
  override_params: JsonObject;
  gpu_share_count: number | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string | null;
};

// 결과 표 한 행. 단건 view 와 grain 이 다르다. BE 가 세션·정책·데이터셋·시나리오를 조인해
// 만든 표시용 요약이다. 그래서 branch/hash/dataset/task/docker tag 는 여기에만 있다.
export type TrainingEvalRunSummaryView = {
  id: string;
  // MLP-829 배포 전 응답에는 이 필드가 없다. 필드 부재는 배포 전 상태이고, null 은 실제 값 부재다.
  // @compat MLP-829 until=be:4399f2f1
  config_name?: string | null;
  source_session_id: string | null;
  source_session_name: string | null;
  policy_name: string | null;
  epoch: number | null;
  budget_unit: TrainingBudgetUnit;
  source_session_total_epochs: number | null;
  source_session_total_steps: number | null;
  branch: string | null;
  git_short_hash: string | null;
  // 상류 카탈로그 이름. training 에서 직접 만든 데이터셋의 실제 이름이다(`alba_v3`).
  // registry 내부 등록 이름이면 서버가 null 로 바꾼다(내부 이름공간).
  upstream_dataset_label: string | null;
  // registry 원본이 있으면 그 참조다. 표시명과 상세 링크를 제공한다.
  dataset_registry_ref: RegistryDatasetRef | null;
  task: string | null;
  docker_tag: string | null;
  status: string;
  scenario: string | null;
  success_rate: number | null;
  // 성공률이 비어 있는 이유. ready/run_pending/harvesting/unavailable/not_applicable 중 하나다.
  success_rate_state: string | null;
  // 실행 중 env 결과로 계산한 현재 성공률. 최종 success_rate가 생기기 전 표에서 사용한다.
  progress_success_rate: number | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string | null;
};

export type TrainingEvalBaselineKey = {
  configName: string;
  datasetUuid: string;
  task: string;
};

export type TrainingEvalBaselineView = {
  session: {
    id: string;
    name: string | null;
    workspace_id: string;
    branch: string | null;
    git_short_hash: string | null;
    created_at: string | null;
  } | null;
  group: {
    scenario_id: string | null;
    scenario_name: string | null;
    policy_id: string | null;
    epoch: number | null;
  } | null;
  runs: TrainingEvalRunSummaryView[];
  truncated: boolean;
};

export async function getTrainingEvalBaseline(
  key: TrainingEvalBaselineKey,
): Promise<TrainingEvalBaselineView> {
  const response = await apiClient.get<TrainingEvalBaselineView>(apiRoutes.training.evalBaseline, {
    params: {
      config_name: key.configName,
      dataset_uuid: key.datasetUuid,
      task: key.task,
    },
  });
  return response.data;
}

// 결과 표 필터. 모든 항목이 선택이다. BE 가 training 으로 그대로 전달한다.
//
// 앞의 여덟 항목은 결과 표가 칩으로 추가하는 자유 텍스트 조건이다. memberUuid·experimentId 는
// 두 Evaluation 탭이 공유하는 페이지 축이다. 이 두 축은 칩이 아니라 드롭다운이 소유한다
// (evalRunFilterFields). memberUuid 는 Team 에서만 보낸다. Me 에서는 BE 가 actor 가 선점한
// workspace 의 합집합을 계산한다.
export type TrainingEvalRunFilters = {
  session?: string;
  branch?: string;
  hash?: string;
  dataset?: string;
  task?: string;
  dockerTag?: string;
  status?: string;
  // 평가된 정책 이름 부분일치. training 이 정책을 조인한 결과에서 해석한다.
  policyName?: string;
  memberUuid?: string;
  experimentId?: string;
};

// 결과 표 정렬 축. BE 의 summary 필드명과 1:1 이다. 소요(duration)는 파생값이라 서버가 정렬하지
// 않는다. 이 목록은 BE 가 받는 축이라 FE 열과 1:1 이 아니다. 결과 표는 세션 이름 대신 정책
// 이름을 표시한다. 그래서 `source_session_name` 에 매핑된 열이 없다(`SORT_FIELD_BY_COLUMN`).
export type TrainingEvalRunSortField =
  | "source_session_name"
  | "epoch"
  | "branch"
  | "dataset_name"
  | "task"
  | "docker_tag"
  | "started_at"
  | "success_rate"
  | "status";

// 정렬 파라미터. `<field>_<asc|desc>` 단일 문자열이다(북마크 목록과 같은 컨벤션).
export type TrainingEvalRunSort =
  | `${TrainingEvalRunSortField}_asc`
  | `${TrainingEvalRunSortField}_desc`;

// 실행 중 진행률. 사이드카가 아무것도 올리지 못한 실행은 source="missing" 이고 수치가 비어 있다.
export type TrainingEvalEnvProgressView = {
  index: number;
  done: number;
  target: number;
  success: number;
  fp: number;
  fail: number;
  timeout: number;
};

export type TrainingEvalRunProgressView = {
  eval_run_id: string;
  status: string;
  phase: string;
  completed: number | null;
  total: number | null;
  remaining: number | null;
  percent: number | null;
  eta_seconds: number | null;
  source: string;
  updated_at: string | null;
  heartbeat_age_seconds: number | null;
  stale: boolean;
  observed_error: string | null;
  num_envs: number | null;
  per_env_targets: number[] | null;
  episodes_per_env: number | null;
  envs: TrainingEvalEnvProgressView[] | null;
};

// success-term 분포. 스칼라 term 만 values/stats 에 값이 있다(vector·unsupported 는 비어 있음).
export type TrainingEvalTermStatsView = {
  min: number | null;
  q1: number | null;
  median: number | null;
  q3: number | null;
  max: number | null;
  mean: number | null;
};

export type TrainingEvalTermPointView = {
  episode_array_index: number;
  value: number;
  env_id: number | null;
  episode_index: number | null;
  outcome: string | null;
  pending_reason: string | null;
};

export type TrainingEvalTermView = {
  name: string;
  kind: string;
  count: number;
  values: TrainingEvalTermPointView[];
  stats: TrainingEvalTermStatsView | null;
  outcome_counts: Record<string, number>;
  raw_preview: unknown;
  warning: string | null;
};

export type TrainingEvalEpisodeView = {
  index: number;
  env_id: number | null;
  episode_index: number | null;
  outcome: string | null;
  pending_reason: string | null;
  // 종료 시점의 sim 시간(초). -1 은 auto-reset으로 측정 불가, null 은 미기록이다.
  elapsed_s: number | null;
};

// 계산 전이면 episodes/terms 가 빈 배열로 온다. 오류가 아니다.
export type TrainingEvalSuccessTermsView = {
  eval_run_id: string;
  run_id: string | null;
  task: string | null;
  scenario: string | null;
  episodes: TrainingEvalEpisodeView[];
  terms: TrainingEvalTermView[];
  warnings: string[];
};

// 세션이 만든 체크포인트. epoch 은 경로 세그먼트 문자열(`epoch_050`)이다. 카탈로그에 등록된
// 정책의 artifact 가 태그 디렉터리 밖에 있으면 상류가 null 을 준다. registry 도 null 을 그대로
// 전달한다.
export type TrainingSessionCheckpointView = {
  epoch: string | null;
  size_bytes: number | null;
  source: string | null;
  artifact_uri: string | null;
};

export type TrainingSessionPoliciesView = {
  session_id: string;
  run_id: string | null;
  experiment_id: string | null;
  mlflow_ui_url: string | null;
  policies: TrainingSessionCheckpointView[];
};

// metrics 는 대표 집계, metrics_all 은 전체다. FE 가 이 값으로 박스플롯/그래프를 그린다(SVG).
export type TrainingEvalMetricsView = {
  eval_run_id: string;
  status: string;
  scenario: string | null;
  kind: EvalRunKind | null;
  mlflow_run_id: string | null;
  mlflow_status: string | null;
  runtime_ms: number | null;
  started_at: string | null;
  finished_at: string | null;
  metrics: Record<string, number>;
  metrics_all: Record<string, number>;
};

// 커서 폴링 응답. eof(terminal)면 폴링을 멈춘다. next_cursor 를 다음 since 로 전달한다.
export type TrainingEvalLogsView = {
  lines: string[];
  next_cursor: string | null;
  eof: boolean;
};

// 세션 단위 eval 집계(by_status·scenarios·run 별 metrics).
export type TrainingEvalSummaryView = {
  session_id: string;
  total: number;
  by_status: Record<string, number>;
  scenarios: string[];
  eval_runs: TrainingEvalMetricsView[];
};

// 영상 행. signed_url 로 직접 받는다(registry 는 byte 를 중계하지 않음). failed/recorded 가 함께 쓴다.
export type TrainingEvalVideoView = {
  filename: string | null;
  kind: string | null;
  content_type: string | null;
  size_bytes: number | null;
  signed_url: string | null;
  outcome: string | null;
  reason: string | null;
  is_failure: boolean | null;
  failure_reasons: string[];
};

export type TrainingEvalScenarioView = {
  id: string;
  name: string;
  description: string | null;
  benchmark_image: string | null;
  fixed_env_vars: Record<string, string>;
  tunable_env_vars: Record<string, string>;
  is_active: boolean | null;
};

export type TrainingScenarioImageTagView = {
  tag: string;
  image_uri: string;
  version: string | null;
};

export type TrainingScenarioImagesView = {
  kind: string | null;
  image: string | null;
  tags: TrainingScenarioImageTagView[];
};

export type TrainingImageMetadataParamView = {
  name: string;
  type: string | null;
  label: string | null;
  default: unknown;
  description: string | null;
  min: number | null;
  max: number | null;
  options_from: string | null;
  options: string[] | null;
};

export type TrainingImageMetadataTaskView = {
  name: string;
  description: string | null;
  success_criteria: TrainingSuccessCriteriaView | null;
  // 과거 image metadata 에는 분류와 단계 순서가 없다. 선택·실행은 그대로 허용한다.
  kind?: string | null;
  progress_stages_order?: string[] | null;
};

export type TrainingSuccessCriteriaValue = string | number | boolean;

export type TrainingSuccessCriteriaParameterContractView = {
  value: TrainingSuccessCriteriaValue;
  operator: string;
  description: string | null;
};

export type TrainingSuccessCriteriaView = {
  parameters: Record<string, TrainingSuccessCriteriaValue>;
  parameter_contracts: Record<string, TrainingSuccessCriteriaParameterContractView>;
};

export type TrainingImageMetadataView = {
  params: TrainingImageMetadataParamView[];
  tasks: TrainingImageMetadataTaskView[];
};

export type MlflowArtifactTextView = {
  body: string;
};

// 학습 진행/ETA 상세. MLflow metric history 에서 계산하며 모든 값이 null 일 수 있다.
export type TrainingSessionProgressDetailView = {
  session_id: string;
  status: string | null;
  phase: string | null;
  run_id: string | null;
  current_step: number | null;
  completed_steps: number | null;
  target_step: number | null;
  remaining_steps: number | null;
  avg_step_seconds: number | null;
  eta_seconds: number | null;
  progress_fraction: number | null;
  current_epoch: number | null;
  completed_epochs: number | null;
  total_epochs: number | null;
};

// 학습 지표 key 목록과 마지막 값. run 이 아직 없으면 빈 응답(run_id=null, 빈 keys)이 온다.
// 오류가 아니다.
// run_id 는 mlflow 텍스트 아티팩트 조회 키다.
export type TrainingSessionMetricsView = {
  session_id: string;
  run_id: string | null;
  metric_keys: string[];
  last_values: Record<string, number>;
};

// metric history 의 점 1개. step 축 값과 기록 시각(epoch ms)을 담는다.
export type TrainingMetricPointView = {
  step: number;
  value: number;
  timestamp: number | null;
};

// 지표 1개의 step 시계열. BE 가 dedupe·정렬한 그대로 온다. 모르는 key 이거나 run 이 없으면
// points 가 비어 있다.
export type TrainingSessionMetricHistoryView = {
  session_id: string;
  run_id: string | null;
  metric_key: string;
  points: TrainingMetricPointView[];
};

// 실패 원인 진단(평탄한 형태). 분류 전이면 classification/reason/retryable 가 모두 null 이다.
// user_action 은 training 이 만든 한국어 안내 문자열이다.
//
// `retry_recommendation`은 classification/retryable의 파생값이므로 FE 계약에서 제외한다.
export type TrainingSessionDiagnosticsView = {
  session_id: string;
  status: string | null;
  code: string | null;
  message: string | null;
  classification: string | null;
  reason: string | null;
  retryable: boolean | null;
  user_action: string | null;
  step_error_message: string | null;
  attempt_error_message: string | null;
};

// 세션 pod 가 쓰는 GPU 1개의 순간 지표다. 측정값이 없는 필드는 null 이다.
export type TrainingSessionGpuView = {
  accelerator_id: string | null;
  model: string | null;
  util_pct: number | null;
  mem_used_bytes: number | null;
  mem_total_bytes: number | null;
};

// 세션 학습 pod 1개의 순간 지표다. `role`·`rank` 는 분산 학습에서 pod 의 역할과 순번이다.
// 예) `"master"`, `"0"`. `cpu_cores`·`mem_used_bytes` 는 사용량이고 request·limit 은 pod 의 요청과
// 상한이다.
export type TrainingSessionPodMetricsView = {
  pod: string;
  phase: string | null;
  role: string | null;
  rank: string | null;
  cpu_cores: number | null;
  mem_used_bytes: number | null;
  cpu_request_cores: number | null;
  cpu_limit_cores: number | null;
  mem_request_bytes: number | null;
  mem_limit_bytes: number | null;
  gpus: TrainingSessionGpuView[];
};

// 값은 요청 시점의 순간값이다. 시계열이 아니다. Running pod 가 없는 세션(대기·종료)은 `pods` 가
// 빈다. 오류가 아니다.
export type TrainingSessionGpuMetricsView = {
  session_id: string;
  pods: TrainingSessionPodMetricsView[];
};

// 평가 트리거 요청(session-centric). kind 에 따른 규칙은 다음과 같다:
//   - general: dataset_id 금지
//   - dataset_bound: dataset_id 필수
// 규칙을 어기면 BE 가 422 를 준다.
export type TriggerEvalRunReq = {
  scenario: string;
  scenario_id?: string | null;
  kind: EvalRunKind;
  dataset_id?: string | null;
  params?: JsonObject;
  execution?: JsonObject | null;
};

// 재실행 override. 모든 항목이 선택이다. 빈 본문이면 원본 설정으로 다시 실행한다.
export type RerunEvalRunReq = {
  seed?: number | null;
  seeds?: number[] | null;
  num_episodes?: number | null;
  scenario_subset?: string[] | null;
  timeout_sec?: number | null;
  gpu_share_count?: number | null;
  sim_benchmark_config_overrides?: JsonObject | null;
  reason?: string | null;
};

export type ReconcileEvalScenariosReq = {
  benchmark_image: string;
  // 평가 폼의 원값. training-infra 가 문자열로 바꾼다. 그래서 여기서 미리 문자열로 바꾸지 않는다.
  // 이름 해시는 원값을 문자열로 바꾼 결과에서 계산한다. 미리 바꾸면 같은 설정이 다른 scenario
  // 가 된다. seed 는 별도 필드가 아니라 `SIMULATION_SEED`/`SEED` 에 콤마로 구분해 넣는다.
  params: Record<string, string | number | boolean>;
};

export type TrainingEvalScenarioReconcileItemView = {
  seed: number;
  // 이미지 메타 기본값까지 채운 환경 변수 전체. scenario 가 어느 슬롯에 담았는지와
  // 관계없이 실행에는 이 값을 쓴다.
  env: Record<string, string>;
  scenario: TrainingEvalScenarioView;
};

export type TrainingEvalScenarioReconcileView = {
  items: TrainingEvalScenarioReconcileItemView[];
  warnings: string[];
};

export type EvalRunLogsQuery = {
  since?: string;
  limit?: number;
};

// ===========================================================================
// eval-run (실행 단위). owner-gate: 귀속 세션이 actor 스코프인지 확인한다.
// ===========================================================================

// My/Team 스코프 eval-run 목록. 결과 표가 서버 페이지 소스로 쓴다.
export async function listTrainingEvalRuns(
  scope: TrainingEvalRunScope,
  query: PageQuery,
  filters: TrainingEvalRunFilters = {},
  sort?: TrainingEvalRunSort,
  policyIds: readonly string[] = [],
  options: { signal?: AbortSignal } = {},
): Promise<PageResp<TrainingEvalRunSummaryView>> {
  const params: Record<string, string | number | string[]> = {};
  if (query.page !== undefined) {
    params.page = query.page;
  }
  if (query.size !== undefined) {
    params.size = query.size;
  }
  // 정렬 파라미터는 `<field>_<asc|desc>` 단일 문자열이다(북마크 목록과 같은 컨벤션).
  // BE 가 `sort` 를 지원해야 실제로 정렬된다. 지원하지 않으면 BE 가 무시하고 기본 순서로 준다.
  if (sort) {
    params.sort = sort;
  }
  if (scope.kind === "team") {
    params.group_uuid = scope.groupUuid;
  }
  if (filters.session) {
    params.session = filters.session;
  }
  if (filters.branch) {
    params.branch = filters.branch;
  }
  if (filters.hash) {
    params.hash = filters.hash;
  }
  if (filters.dataset) {
    params.dataset = filters.dataset;
  }
  if (filters.task) {
    params.task = filters.task;
  }
  if (filters.dockerTag) {
    params.docker_tag = filters.dockerTag;
  }
  if (filters.status) {
    params.status = filters.status;
  }
  if (filters.policyName) {
    params.policy_name = filters.policyName;
  }
  // "전체" 센티널은 빈 문자열이라 falsy 다. 그래서 와이어로 보내지 않는다.
  if (scope.kind === "team" && filters.memberUuid) {
    params.member_uuid = filters.memberUuid;
  }
  if (filters.experimentId) {
    params.experiment_id = filters.experimentId;
  }
  if (policyIds.length > 0) {
    params.policy_id = [...policyIds];
  }
  const response = await apiClient.get<PageResp<TrainingEvalRunSummaryView>>(
    scope.kind === "me" ? apiRoutes.training.meEvalRuns : apiRoutes.training.teamEvalRuns,
    { params, signal: options.signal },
  );
  return response.data;
}

export async function getTrainingEvalRunSummaries(
  evalRunIds: readonly string[],
): Promise<TrainingEvalRunSummaryView[]> {
  const response = await apiClient.get<TrainingEvalRunSummaryView[]>(
    apiRoutes.training.evalRunSummaries,
    { params: { eval_run_id: evalRunIds } },
  );
  return response.data;
}

// BE `GET /eval-runs/summaries` 의 eval_run_id 반복 파라미터 상한.
const EVAL_RUN_SUMMARIES_CHUNK_SIZE = 15;

/**
 * 개수 제한 없이 eval-run 요약을 배치로 받는다.
 *
 * 반복 파라미터 상한을 넘는 id 목록을 청크로 나눠 **병렬 1단계**로 보낸다. 항목당 요청
 * (network N+1)과 청크를 차례로 보내는 waterfall 을 만들지 않는다(rules.md §7). 한 청크라도
 * 실패하면 전체가 실패한다. 일부만 갱신된 표에서는 어느 값이 최신인지 알 수 없다.
 *
 * Args:
 *   evalRunIds: 조회할 eval-run id 들.
 *
 * Returns:
 *   요청한 id 들의 요약. 서버가 모르는 id 는 결과에 없다.
 */
export async function getTrainingEvalRunSummariesBatched(
  evalRunIds: readonly string[],
): Promise<TrainingEvalRunSummaryView[]> {
  if (evalRunIds.length === 0) {
    return [];
  }
  const chunks: string[][] = [];
  for (let start = 0; start < evalRunIds.length; start += EVAL_RUN_SUMMARIES_CHUNK_SIZE) {
    chunks.push(evalRunIds.slice(start, start + EVAL_RUN_SUMMARIES_CHUNK_SIZE));
  }
  const pages = await Promise.all(chunks.map((chunk) => getTrainingEvalRunSummaries(chunk)));
  return pages.flat();
}

export async function getTrainingEvalRunProgress(
  evalRunId: string,
): Promise<TrainingEvalRunProgressView> {
  const response = await apiClient.get<TrainingEvalRunProgressView>(
    apiRoutes.training.evalRunProgress(evalRunId),
  );
  return response.data;
}

export async function getTrainingEvalRunSuccessTerms(
  evalRunId: string,
): Promise<TrainingEvalSuccessTermsView> {
  const response = await apiClient.get<TrainingEvalSuccessTermsView>(
    apiRoutes.training.evalRunSuccessTerms(evalRunId),
  );
  return response.data;
}

export async function listTrainingSessionCheckpoints(
  sessionId: string,
): Promise<TrainingSessionPoliciesView> {
  const response = await apiClient.get<TrainingSessionPoliciesView>(
    apiRoutes.training.sessionCheckpoints(sessionId),
  );
  return response.data;
}

export async function getTrainingEvalRun(evalRunId: string): Promise<TrainingEvalRunView> {
  const response = await apiClient.get<TrainingEvalRunView>(apiRoutes.training.evalRun(evalRunId));
  return response.data;
}

export async function getTrainingEvalRunMetrics(
  evalRunId: string,
): Promise<TrainingEvalMetricsView> {
  const response = await apiClient.get<TrainingEvalMetricsView>(
    apiRoutes.training.evalRunMetrics(evalRunId),
  );
  return response.data;
}

// 커서 폴링. since(직전 next_cursor)·limit(1..5000, 기본 200)는 선택이다.
export async function getTrainingEvalRunLogs(
  evalRunId: string,
  query: EvalRunLogsQuery = {},
): Promise<TrainingEvalLogsView> {
  const params: Record<string, string | number> = {};
  if (query.since) {
    params.since = query.since;
  }
  if (query.limit !== undefined) {
    params.limit = query.limit;
  }
  const response = await apiClient.get<TrainingEvalLogsView>(
    apiRoutes.training.evalRunLogs(evalRunId),
    { params },
  );
  return response.data;
}

export async function listTrainingEvalRunFailedVideos(
  evalRunId: string,
  scenario?: string,
): Promise<TrainingEvalVideoView[]> {
  const params: Record<string, string> = {};
  if (scenario) {
    params.scenario = scenario;
  }
  const response = await apiClient.get<TrainingEvalVideoView[]>(
    apiRoutes.training.evalRunFailedVideos(evalRunId),
    { params },
  );
  return response.data;
}

export async function listTrainingEvalRunRecordedVideos(
  evalRunId: string,
  scenario?: string,
): Promise<TrainingEvalVideoView[]> {
  const params: Record<string, string> = {};
  if (scenario) {
    params.scenario = scenario;
  }
  const response = await apiClient.get<TrainingEvalVideoView[]>(
    apiRoutes.training.evalRunRecordedVideos(evalRunId),
    { params },
  );
  return response.data;
}

export async function cancelTrainingEvalRun(evalRunId: string): Promise<TrainingEvalRunView> {
  const response = await apiClient.post<TrainingEvalRunView>(
    apiRoutes.training.evalRunCancel(evalRunId),
  );
  return response.data;
}

export async function rerunTrainingEvalRun(
  evalRunId: string,
  payload: RerunEvalRunReq,
): Promise<TrainingEvalRunView> {
  const response = await apiClient.post<TrainingEvalRunView>(
    apiRoutes.training.evalRunRerun(evalRunId),
    payload,
  );
  return response.data;
}

// ===========================================================================
// 세션별 평가 (training session id)
// ===========================================================================

export async function listTrainingSessionEvalRuns(
  sessionId: string,
): Promise<TrainingEvalRunView[]> {
  const response = await apiClient.get<TrainingEvalRunView[]>(
    apiRoutes.training.sessionEvalRuns(sessionId),
  );
  return response.data;
}

export async function getTrainingSessionEvalSummary(
  sessionId: string,
): Promise<TrainingEvalSummaryView> {
  const response = await apiClient.get<TrainingEvalSummaryView>(
    apiRoutes.training.sessionEvalSummary(sessionId),
  );
  return response.data;
}

export async function getTrainingSessionProgress(
  sessionId: string,
): Promise<TrainingSessionProgressDetailView> {
  const response = await apiClient.get<TrainingSessionProgressDetailView>(
    apiRoutes.training.sessionProgress(sessionId),
  );
  return response.data;
}

export async function getTrainingSessionMetrics(
  sessionId: string,
): Promise<TrainingSessionMetricsView> {
  const response = await apiClient.get<TrainingSessionMetricsView>(
    apiRoutes.training.sessionMetrics(sessionId),
  );
  return response.data;
}

export async function getTrainingSessionMetricHistory(
  sessionId: string,
  metricKey: string,
): Promise<TrainingSessionMetricHistoryView> {
  const response = await apiClient.get<TrainingSessionMetricHistoryView>(
    apiRoutes.training.sessionMetricsHistory(sessionId),
    { params: { metric_key: metricKey } },
  );
  return response.data;
}

export async function getTrainingSessionDiagnostics(
  sessionId: string,
): Promise<TrainingSessionDiagnosticsView> {
  const response = await apiClient.get<TrainingSessionDiagnosticsView>(
    apiRoutes.training.sessionDiagnostics(sessionId),
  );
  return response.data;
}

// 서버에 캐시가 없다. 요청마다 상류가 Kubernetes 와 Prometheus 를 조회한다. 그래서 15초보다 짧은
// 간격으로 부르지 않는다.
export async function getTrainingSessionGpuMetrics(
  sessionId: string,
  options: { signal?: AbortSignal } = {},
): Promise<TrainingSessionGpuMetricsView> {
  const response = await apiClient.get<TrainingSessionGpuMetricsView>(
    apiRoutes.training.sessionGpuMetrics(sessionId),
    { signal: options.signal },
  );
  return response.data;
}

// 세션 학습 로그 커서 폴링. eval-run 로그와 같은 {lines, next_cursor, eof} 계약이다.
export async function getTrainingSessionLogs(
  sessionId: string,
  query: EvalRunLogsQuery = {},
): Promise<TrainingEvalLogsView> {
  const params: Record<string, string | number> = {};
  if (query.since) {
    params.since = query.since;
  }
  if (query.limit !== undefined) {
    params.limit = query.limit;
  }
  const response = await apiClient.get<TrainingEvalLogsView>(
    apiRoutes.training.sessionLogs(sessionId),
    { params },
  );
  return response.data;
}

// 세션-중심 평가 트리거(201).
export async function triggerTrainingSessionEvalRun(
  sessionId: string,
  payload: TriggerEvalRunReq,
): Promise<TrainingEvalRunView> {
  const response = await apiClient.post<TrainingEvalRunView>(
    apiRoutes.training.sessionEvalRuns(sessionId),
    payload,
  );
  return response.data;
}

// ===========================================================================
// eval scenario (전역 카탈로그) + mlflow 텍스트
// ===========================================================================

export async function listTrainingEvalScenarios(
  activeOnly?: boolean,
): Promise<TrainingEvalScenarioView[]> {
  const params: Record<string, boolean> = {};
  if (activeOnly !== undefined) {
    params.active_only = activeOnly;
  }
  const response = await apiClient.get<TrainingEvalScenarioView[]>(
    apiRoutes.training.evalScenarios,
    { params },
  );
  return response.data;
}

// 시나리오 카탈로그·이미지 태그·태그별 metadata 는 전역이고 거의 바뀌지 않는다. 그래서 세션
// 캐시를 둔다. rules.md §7 에 따라 Policy 카탈로그와 Evaluation Results 는 창·탭마다 같은 목록을
// 다시 받지 않는다. 계약은 listAllLabels 와 같다: 캐시 + in-flight 공유 + 무효화 세대 가드.
const scenarioCatalog: CatalogSlot<readonly TrainingEvalScenarioView[]> = emptySlot();
const scenarioImagesByKind = new Map<string, CatalogSlot<TrainingScenarioImagesView>>();
const imageMetadataByTag = new Map<string, CatalogSlot<TrainingImageMetadataView>>();
// 태그별 metadata 는 태그 수만큼 늘어난다. 삽입 순서 기준 상한을 두어 오래 열린 탭에서 캐시가
// 계속 커지지 않게 한다.
const IMAGE_METADATA_MAX_ENTRIES = 32;

type CatalogSlot<T> = {
  cache: T | null;
  inflight: Promise<T> | null;
  // 무효화할 때마다 증가한다. 무효화 전에 시작한 요청의 응답이 새 세대 캐시를 덮지 못하게 한다.
  epoch: number;
};

function emptySlot<T>(): CatalogSlot<T> {
  return { cache: null, inflight: null, epoch: 0 };
}

function loadCatalogOnce<T>(slot: CatalogSlot<T>, fetcher: () => Promise<T>): Promise<T> {
  if (slot.cache !== null) {
    return Promise.resolve(slot.cache);
  }
  if (slot.inflight !== null) {
    return slot.inflight;
  }
  const startEpoch = slot.epoch;
  const request = fetcher()
    .then((value) => {
      if (slot.epoch === startEpoch) {
        slot.cache = value;
      }
      return value;
    })
    .finally(() => {
      if (slot.epoch === startEpoch) {
        slot.inflight = null;
      }
    });
  slot.inflight = request;
  return request;
}

function clearSlot<T>(slot: CatalogSlot<T>): void {
  slot.epoch += 1;
  slot.cache = null;
  slot.inflight = null;
}

function slotFor<T>(
  slots: Map<string, CatalogSlot<T>>,
  key: string,
  maxEntries = 0,
): CatalogSlot<T> {
  let slot = slots.get(key);
  if (slot === undefined) {
    slot = emptySlot<T>();
    slots.set(key, slot);
    while (maxEntries > 0 && slots.size > maxEntries) {
      const oldest = slots.keys().next().value;
      if (oldest === undefined) {
        break;
      }
      slots.delete(oldest);
    }
  }
  return slot;
}

// 전역 시나리오 카탈로그. 상세 창·비교 창·평가 제출이 공유하며 세션 동안 한 번만 받는다.
export function listAllTrainingEvalScenarios(): Promise<readonly TrainingEvalScenarioView[]> {
  return loadCatalogOnce(scenarioCatalog, () => listTrainingEvalScenarios(false));
}

// 세션 종료·사용자 전환 때 호출한다. 진행 중인 요청이 나중에 캐시에 쓰는 것도 막는다.
export function invalidateTrainingEvalCatalogs(): void {
  clearSlot(scenarioCatalog);
  scenarioImagesByKind.clear();
  imageMetadataByTag.clear();
}

// 평가 설정 하나로 seed 별 시나리오를 받는다. seed 수와 관계없이 왕복은 한 번이다.
// training-infra 가 seed 별로 시나리오를 찾거나 만든다. TASK 누락이나 보관된 이름 점유로 오는
// 400 은 글로벌 핸들러가 문구로 바꾼다.
export async function reconcileTrainingEvalScenarios(
  payload: ReconcileEvalScenariosReq,
): Promise<TrainingEvalScenarioReconcileView> {
  const response = await apiClient.post<TrainingEvalScenarioReconcileView>(
    apiRoutes.training.evalScenarioReconcile,
    payload,
  );
  rememberScenarios(response.data.items.map((item) => item.scenario));
  return response.data;
}

// reconcile 결과를 이미 받은 카탈로그에 합친다. 카탈로그를 아직 받지 않았으면 아무것도 하지
// 않는다. 무효화하지 않고 합치는 이유가 있다. 무효화하면 상세·비교 창이 열릴 때 전체
// 카탈로그를 다시 받는다. 32-seed 를 한 건씩 합치면 카탈로그를 32번 복사한다. 그래서 결과를
// 한 번에 받아 한 번만 복사한다.
function rememberScenarios(scenarios: readonly TrainingEvalScenarioView[]): void {
  const cached = scenarioCatalog.cache;
  if (cached === null) {
    return;
  }
  const next = Array.from(cached);
  const indexByName = new Map(next.map((entry, index) => [entry.name, index]));
  for (const scenario of scenarios) {
    const index = indexByName.get(scenario.name);
    if (index === undefined) {
      indexByName.set(scenario.name, next.length);
      next.push(scenario);
    } else {
      next[index] = scenario;
    }
  }
  scenarioCatalog.cache = next;
}

export function listTrainingScenarioImages(kind?: string): Promise<TrainingScenarioImagesView> {
  return loadCatalogOnce(slotFor(scenarioImagesByKind, kind ?? ""), async () => {
    const params: Record<string, string> = {};
    if (kind) {
      params.kind = kind;
    }
    const response = await apiClient.get<TrainingScenarioImagesView>(
      apiRoutes.training.evalScenarioImages,
      { params },
    );
    return response.data;
  });
}

// image tag 는 바뀌지 않는다. 그래서 태그별 metadata 는 세션 동안 캐시해도 안전하다.
export function getTrainingScenarioImageMetadata(tag: string): Promise<TrainingImageMetadataView> {
  return loadCatalogOnce(slotFor(imageMetadataByTag, tag, IMAGE_METADATA_MAX_ENTRIES), async () => {
    const response = await apiClient.get<TrainingImageMetadataView>(
      apiRoutes.training.evalScenarioImageMetadata(tag),
    );
    return response.data;
  });
}

// 작은 텍스트 아티팩트(config·box-plot). auth-only 다. run_id 를 세션에 매핑할 수 없기 때문이다.
export async function getTrainingMlflowArtifactText(
  runId: string,
  path: string,
): Promise<MlflowArtifactTextView> {
  const response = await apiClient.get<MlflowArtifactTextView>(
    apiRoutes.training.mlflowArtifactText,
    { params: { run_id: runId, path } },
  );
  return response.data;
}
